// Patient / Resident types - mirrors TblPatient from C# (System.Text.Json camelCase)

export interface Patient {
  [key: string]: unknown;
  pKey_Patient: number;
  fKey_Facility: number;

  // Name
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  preferredName: string | null;
  fullName: string | null;

  // Location
  floor: string | null;
  wing: string | null;
  unit: string | null;
  room_No: number | null;
  bed_No: string | null;
  fKey_Unit: number;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;

  // Demographics
  birthDate: string | null;
  age: number;
  gender: string | null;

  // Weight & Height
  weight: number;
  admitWgt: number;
  weight_ReAdmit: number;
  weight_Goal: number;
  // ± tolerance around `weight_Goal` (whole pounds). Surfaces
  // as a small "±" spin edit next to Goal Weight in the Weights
  // sub-group and as a `±N` suffix in the Weight History tab.
  weightGoalPlusMinus: number;
  heightInInches: number;
  heightFeets: number;
  iBW_Start: number;
  iBW_End: number;
  adjusted_Wgt: number;
  iBWManuallyAdjusted: boolean;
  adjustedWeightManuallyAdjusted: boolean;
  usualBodyWeight: number;
  // Free-text "History" note on the Weights sub-group (Physical/
  // Frame/Weights tab). `Weight_History` column on tblPatient,
  // nvarchar(25). FirstCharLowerPolicy → `weight_History`.
  weight_History: string | null;
  frame: string | null;

  // Dates
  admitDate: string | null;
  discharge_Date: string | null;
  reAdmit_Date: string | null;
  lastUpdated: string | null;
  /** tblPatient.TS_Patient as base64. Sent back unchanged on AddMod so a
   *  save over someone else's newer edit gets HTTP 409 instead of
   *  overwriting it. Absent on older API builds. */
  rowVersion?: string | null;
  deleted_Date: string | null;

  // Status flags
  discharged: boolean;
  isDeleted: boolean;
  /** Soft deleted and queued for the permanent purge. Counts as deleted wherever
   *  isDeleted does; optional because only some endpoints fill it in. */
  readyForPermDelete?: boolean;
  actAsTemplate: boolean;
  isBulkOrder: boolean;
  isBulk1stChoice: boolean;
  tF_Tube_Feed: boolean;
  staticSelectiveMenu: boolean;

  // Guest link (null = not a guest, set = guest of this patient)
  fKey_Patient: number | null;

  // Diet
  dietOrder: string | null;
  fKey_Diet_Type1: number | null;
  fKey_Diet_Type2: number | null;
  fKey_Diet_Type3: number | null;
  fKey_Diet_Type4: number | null;
  fKey_Diet_Type5: number | null;
  fKey_Consist1: number | null;
  fKey_Consist2: number | null;
  fKey_Consist3: number | null;
  fKey_Consist4: number | null;
  fKey_Consist5: number | null;
  fKey_Doctor: number | null;
  res_ID: string | null;
  linked_ID: string | null;
  imagePath: string | null;

  // Drug Allergies
  drugAllergies: string | null;

  // Calorie / Protein / Fluid needs
  a_Factor: number;
  i_Factor: number;
  protein_Condition: number;
  protein_Exceptions: number;
  fluid_Condition: number;
  needsCalorieWeightToUse: number;
  needsProteinWeightToUse: number;
  needsFluidsWeightToUse: number;
  calorie_EnergyCalculation: number;
  calorie_EnergyPerKilogramRange: number;
  calorie_RangeLow: number;
  calorie_RangeHigh: number;
  calorie_Other: string | null;
  protein_Condition_Other: string | null;
  protein_Condition_AlbuminLow: number;
  protein_Condition_AlbuminHigh: number;
  protein_Condition_ProteinLow: number;
  protein_Condition_ProteinHigh: number;
  fluid_Condition_Other: string | null;
  fluid_Condition_FluidLow: number;
  fluid_Condition_FluidHigh: number;
  mealPlanProvidesCalories: string | null;
  mealPlanProvidesProtein: string | null;
  mealPlanProvidesFluids: string | null;

  // Acceptance — free-text strings with facility-wide MRU autocomplete (matches WinForms Meal_Acpt/Sup_Acpt/Fluid_Acpt)
  meal_Acpt: string | null;
  sup_Acpt: string | null;
  fluid_Acpt: string | null;

  // Advance Directive — free-text with MRU autocomplete (DB: AdvanceDirective)
  advanceDirective: string | null;

  // Religion / Ethnic Background — free-text with MRU autocomplete (DB: Religious_Ethnic)
  religious_Ethnic: string | null;

  // Emergency Contact — canonical column on tblPatient is the
  // single free-text `Emer_Contact_Info` (wire name
  // `emer_Contact_Info` via the API's FirstCharLowerPolicy).
  // The legacy split `emergencyContact` / `emergencyContactPhone`
  // fields are NOT populated from the API — they're held here
  // only because some screens still bind editors to them.
  emer_Contact_Info: string | null;
  emergencyContact: string | null;
  emergencyContactPhone: string | null;

  // Food Allergies (10 slots)
  allergies: string | null;
  fKey_Food_Allergy_1: number | null;
  fKey_Food_Allergy_2: number | null;
  fKey_Food_Allergy_3: number | null;
  fKey_Food_Allergy_4: number | null;
  fKey_Food_Allergy_5: number | null;
  fKey_Food_Allergy_6: number | null;
  fKey_Food_Allergy_7: number | null;
  fKey_Food_Allergy_8: number | null;
  fKey_Food_Allergy_9: number | null;
  fKey_Food_Allergy_10: number | null;
  food_Allergy_1_UserCreated: string | null;
  food_Allergy_2_UserCreated: string | null;
  food_Allergy_3_UserCreated: string | null;
  food_Allergy_4_UserCreated: string | null;
  food_Allergy_5_UserCreated: string | null;
  food_Allergy_6_UserCreated: string | null;
  food_Allergy_7_UserCreated: string | null;
  food_Allergy_8_UserCreated: string | null;
  food_Allergy_9_UserCreated: string | null;
  food_Allergy_10_UserCreated: string | null;

  // Special Devices (5 slots)
  fKey_Spec_Devices_1: number | null;
  fKey_Spec_Devices_2: number | null;
  fKey_Spec_Devices_3: number | null;
  fKey_Spec_Devices_4: number | null;
  fKey_Spec_Devices_5: number | null;
  // Mealtime bitmask: Breakfast=1, Lunch=2, Dinner=4. `All` = 7
  // (DB column default). Maps to the legacy `Mealtime` enum in
  // EnumerationsConstantsCollections.vb. Always stored as int, not
  // a delimited string, even though older code commented otherwise.
  fKey_Spec_Devices_1_Time: number | null;
  fKey_Spec_Devices_2_Time: number | null;
  fKey_Spec_Devices_3_Time: number | null;
  fKey_Spec_Devices_4_Time: number | null;
  fKey_Spec_Devices_5_Time: number | null;

  // Meal info
  meal_Pattern_Type: number;
  comments: string | null;
  diagnosis: string | null;

  // Selective menu notes
  sM_Notes_B: string | null;
  sM_Notes_L: string | null;
  sM_Notes_D: string | null;

  // Tray ticket settings
  tT_Custom_Message: string | null;
  tT_Custom_Message_L: string | null;
  tT_Custom_Message_D: string | null;
  // Per-meal "Tray Ticket Notes" — the dietitian-facing memo on
  // the patient profile. Distinct from TT_Custom_Message (which
  // is a system-generated note) and named per the WinForms
  // mapping: B=Breakfast, L=Lunch, D=Dinner.
  tT_Note_B: string | null;
  tT_Note_L: string | null;
  tT_Note_D: string | null;
  // Per-meal "print a duplicate tray ticket" flag. In WinForms
  // these default from the facility's PrecheckDuplicateTrayTicket
  // setting on new-patient create (see frmDietech_Resident_AddMod).
  tT_Duplicate_B: boolean;
  tT_Duplicate_L: boolean;
  tT_Duplicate_D: boolean;
  // "Show On TT" — controls whether the resident's photo prints
  // on the tray ticket. Defaults to True on new residents in
  // WinForms (ResidentManager.vb sets dr("TT_ShowImage") = True).
  tT_ShowImage: boolean;
  /** True hides the tray ticket's birthday cake and Happy Birthday! note (facility TrayTicket_PrintBDayCake). */
  tT_HideHappyBirthdayNotation?: boolean;
  set_B: number;
  set_L: number;
  set_D: number;
  tray_B: number;
  tray_L: number;
  tray_D: number;

  // Portions
  portion_Size_Break: number;
  portion_Size_Lunch: number;
  portion_Size_Dinner: number;

  // Review intervals
  flag_Weight_Interval: number;
  flag_DietOrderReview_Interval: number;
  flag_Nutritional_Assessment_Interval: number;
  flag_Weight_Temp_Interval: number | null;
  flag_Weight_Temp_Date: string | null;

  // DRI Group
  fKey_RC_DRI_Group: number;

  // Material Colors
  fKey_MatColors_1: number;
  fKey_MatColors_2: number;

  // Lab Data / AccuCheck
  labDataAccuCheck: string | null;
  labDataAccuCheckEAM: string | null;
  labDataAccuCheckAM: string | null;
  labDataAccuCheckPM: string | null;
  labDataAccuCheckHS: string | null;

  // Medications (patient-level)
  medicationInsulinHypoglyemicAgents: string | null;
  medicationVitaminMineralSupplements: string | null;

  // Skin
  skin_Condition: string | null;
  skin_PressureSorePresent: boolean;
  skin_StasisUlcer: string | null;
  skin_NutritionalIntervention: string | null;

  // Physical Data — the resident's value for each
  // facility-configured physical property (tblFacility-
  // PhysicalProperty). Column names on tblPatient are `PP_<name>`
  // with the property's Name stripped of spaces. FirstCharLower-
  // Policy lowercases the leading `P` only, so the wire/TS field
  // name is `pP_<name>`.
  pP_Chewing: string | null;
  pP_Dentition: string | null;
  pP_Swallowing: string | null;
  pP_MentalStatus: string | null;
  pP_Communication: string | null;
  pP_Comprehension: string | null;
  pP_MoodBehavior: string | null;
  pP_Hearing: string | null;
  pP_Vision: string | null;
  pP_FeedingSkills: string | null;
  pP_EatingLocation: string | null;
  pP_SpecialDevices: string | null;
  pP_AmbulationSkills: string | null;
  pP_Incontinent: string | null;
  pP_ProblemsWith: string | null;
  pP_Edema: string | null;
  pP_SkinTurgor: string | null;
  pP_OralMucusMembranes: string | null;
  pP_Hair: string | null;
  pP_Nails: string | null;
  pP_AmputeePercentage: string | null;
  pP_GIStatus: string | null;

  // Flags (NotMapped - computed server-side, may or may not be present)
  flag_DietOrder?: boolean;
  flag_Assessment?: boolean;
  flag_Weight?: boolean;
  flag_TrayLine?: boolean;
  flagMessage?: string | null;
  lastDietOrderDate?: string | null;
  lastAssessmentDate?: string | null;
  lastWeightDate?: string | null;
}

export interface ResidentFlagResult {
  pKey_Patient: number;
  flag_DietOrder: boolean;
  flag_Assessment: boolean;
  flag_Weight: boolean;
  flag_TrayLine: boolean;
  flagMessage: string | null;
  dO_Message: string | null;
  assessment_Message: string | null;
  weight_Message: string | null;
  trayLine_Message: string | null;
}

export interface ResidentUpdateCheck {
  count: number;
  lastModified: string | null;
}

export interface PatientStatusHistory {
  pKey_Status_History: number;
  fKey_Patient: number;
  statusType: string | null;
  dateTimeOfLog: string | null;
  notes: string | null;
  fKey_User: number | null;
  /**
   * Set on rows the EHR processor wrote. The processor matches later
   * discharges and readmits on it, so those rows can't be edited or deleted.
   */
  visitNumber?: string | null;
}

export interface DietFluc {
  pKey_Diet_Flucs: number;
  fKey_Patient: number;
  change_Date: string | null;
  dietOrder: string | null;
  fKey_Diet_Type1: number;
  fKey_Diet_Type2: number;
  fKey_Diet_Type3: number;
  fKey_Diet_Type4: number;
  fKey_Diet_Type5: number;
  fKey_Consist1: number;
  fKey_Consist2: number;
  fKey_Consist3: number;
  fKey_Consist4: number;
  fKey_Consist5: number;
  isLocked: boolean;
  /** 1 Manual, 2 EMR, 3 EMR Adjustment (DIET_FLUC_TYPE). */
  type: number;
  note: string | null;
  ehrDietText: string | null;
  lock_Date: string | null;
  endDate: string | null;
  /** The EHR's order number on orders the EHR processor wrote; empty on manual orders. */
  eHR_OrderID?: string | null;
}

/** tblDiet_Flucs.Type (WinForms frmDietech_Flucts_DataGrid repLuType). */
export const DIET_FLUC_TYPE = { Manual: 1, EMR: 2, EMRAdjustment: 3 } as const;

export interface LogInteraction {
  pKey_Log_Interaction: number;
  fKey_Log_Interaction_Location: number;
  fKey_Log_Interaction_Type: number;
  fKey_Patient: number | null;
  fKey_Notes: number | null;
  fKey_Prd_Adjustment_Global: number | null;
  fKey_Prd_Adjustment_Global_Group: number | null;
  fKey_Prd_Meal_Pattern_Global: number | null;
  fKey_Prd_Meal_Pattern_Global_Group: number | null;
  notes: string | null;
  dateTimeOfLog: string;
  pmSec_User_PKey_User: number;
  pmSec_User_UserName: string | null;
  pmSec_User_FirstName: string | null;
  pmSec_User_LastName: string | null;
  residentFirstName: string | null;
  residentLastName: string | null;
  facilityName: string | null;
  locationName: string | null;
  typeName: string | null;
  userFullName: string | null;
  /** Log transaction FK — non-null when the interaction wrote to
   *  a snapshot table (SM entry, MP save, etc.). Used to join the
   *  detail-view SM history back to the master row. */
  fKey_Log_Transaction: number | null;
}

export interface GuestPatient {
  pKey_Patient: number;
  fKey_Patient: number | null;
  fullName: string | null;
  nextDate: string | null;
  nextMealtimeString: string | null;
  nextIsRecurrence: boolean;
  recurrenceDescription: string | null;
  recurrenceDays: string | null;
  // Recurrence end-by date (server-populated from the schedule
  // row; null for one-time guests or recurrences with no end).
  recurrenceEndsBy?: string | null;
  // Guest photo as a `data:image/jpeg;base64,...` URL, or
  // null / empty when the guest has no photo. Drives the
  // 28px avatar column in GuestDetailSection.
  imagePath?: string | null;
  // Only true when the list was asked for discharged guests too.
  discharged?: boolean;
  // Meals the guest eats on the date the list was asked for (the
  // client's today). All false when the guest has no visit that day.
  mealB?: boolean;
  mealL?: boolean;
  mealD?: boolean;
}

export interface GuestSchedule {
  pKey_Patient_GuestSchedule: number;
  fKey_Patient: number;
  fullName: string | null;
  mealtimesDisplay: string | null;
  specificDate: string | null;
  numberOfGuests: number;
  isRecurrence: boolean;
}

export interface PatientAttendance {
  pKey_Attendance: number;
  fKey_Patient: number;
  dayOfWeek: number;
  mealTime: number;
  attends: boolean;
}

export interface PatientTrayLine {
  pKey_TrayLine: number;
  fKey_Patient: number;
  dayOfWeek: number;
  mealTime: number;
  fKey_TrayLine: number;
  trayLineName: string;
}

// ─── New Tray Line types (matches the WinForms data model)
//
//   • Defaults  → tblPatient.Set_B/L/D, Tray_B/L/D
//   • Overrides → tblPatient_TrayLineOverride rows, one per
//                 (Day × Mealtime) when a particular slot
//                 diverges from the defaults
//
// Mealtime is 0-indexed (0=Breakfast, 1=Lunch, 2=Dinner).
// Day is 1-indexed Monday=1 … Sunday=7 to match the
// legacy ResidentsTrayLineOverride.DayOfWeek enum.
export interface PatientTrayLineDefaults {
  setB: number;
  setL: number;
  setD: number;
  trayB: number;
  trayL: number;
  trayD: number;
}

export interface PatientTrayLineOverrideRow {
  // 0 for unsaved rows; server assigns on insert.
  pKey_Patient_TrayLineOverride: number;
  day: number;
  mealtime: number;
  trayLineSet: number;
  trayLineTray: number;
}

export interface PatientTrayLineEnvelope {
  defaults: PatientTrayLineDefaults;
  overrides: PatientTrayLineOverrideRow[];
}

// Slim DTO for the Manage Deleted grid. Backed by the
// /api/Patient/DeletedSummary endpoint. Carries only the
// columns the grid renders so payload stays tiny on facilities
// with thousands of soft-deleted patients.
export interface PatientDeletedSummary {
  pKey_Patient: number;
  res_ID: string | null;
  fullName: string | null;
  unit: string | null;
  room_No: number | null;
  bed_No: string | null;
  birthDate: string | null;
  age: number | null;
  weight: number | null;
  deleted_Date: string | null;
  /** Comma-separated last-known diet types + consists. */
  dietOrder: string | null;
  /** EHR linkage id from tblPatient.Linked_ID. */
  linked_ID: string | null;
}

// Batch payload for the Assign Tray Line dialog. One row per
// resident whose Set/Tray/Location differs from the dialog's
// open snapshot.
export interface PatientTrayLineAssignmentRow {
  pKey_Patient: number;
  setB: number;
  setL: number;
  setD: number;
  trayB: number;
  trayL: number;
  trayD: number;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;
}

/** Extended attendance row: one meal/nourishment/SM × 7 days */
export interface AttendanceRow {
  /** Meal type: 0=B, 1=L, 2=D, 3=EAM, 4=AM, 5=PM, 6=HS, 7=PM2, 8=HS2 */
  mealtime: number;
  /** Display name from facility config */
  mealtimeName: string;
  /** True for Selective Menu variant rows (only mealtimes 0-2) */
  isSelectiveMenu: boolean;
  /** For nourishments: parent meal (0/1/2). For main meals: -1 */
  associatedMealtime: number;
  /** Display order */
  sortOrder: number;
  /** Whether this nourishment is active for the facility */
  isActive: boolean;
  /** 7-element array: index 0=Mon, 1=Tue ... 6=Sun */
  days: boolean[];
  /** Client-only "pending delete" flags. Only meaningful on
   *  Selective Menu rows (`isSelectiveMenu === true`) and only
   *  where `days[d] === false`. When true, the patient has FUTURE
   *  `tblPrd_Selective_Menu` rows queued for that day-of-week that
   *  would need to be purged. Mirrors WinForms
   *  `SMToBeDeleted_{M..U}` columns in the in-memory DataTable of
   *  ucDietech_Resident_AttendanceNew.vb. Never sent back to the
   *  server on Save — a dedicated "Delete future SM" action calls
   *  the delete endpoint separately, which is when these clear. */
  smToBeDeleted?: boolean[];
}

export interface PatientAttendanceExtended {
  fKey_Patient: number;
  rows: AttendanceRow[];
}

export interface PatientLocationException {
  pKey_Patient_Location_Excpt: number;
  fKey_Patient: number;
  location: string;
  day_1: boolean;
  day_2: boolean;
  day_3: boolean;
  day_4: boolean;
  day_5: boolean;
  day_6: boolean;
  day_7: boolean;
  mT_B: boolean;
  mT_L: boolean;
  mT_D: boolean;
}

export interface DietType {
  pKey_Diet_Type: number;
  name: string | null;
  diet_Type_Name: string | null;
  abbreviation: string | null;
  argb_Back: number;
  argb_Fore: number;
}

export interface Consistency {
  pKey_Consist: number;
  name: string | null;
  consist_Name: string | null;
  abbreviation: string | null;
  argb_Back: number;
  argb_Fore: number;
  /** IDDSI level 0-7 (tblConsist.IddsiLevel); null or absent when unmapped
   *  or when the database has no IDDSI columns. */
  iddsiLevel?: number | null;
  /** IDDSI type: Food / Drink / Both. */
  iddsiCategory?: string | null;
}

export interface FoodAllergy {
  pKey_Food_Allergy: number;
  name: string | null;
  food_Allergy_Name: string | null;
  fKey_Prd_Adjustment_Global: number | null;
  fKey_Prd_Adjustment_Global_Group: number | null;
}

export interface Doctor {
  pKey_Doctor: number;
  doc_Name: string | null;
  /** tblDoctor.FirstName / LastName: the prescriber dialog edits them separately. */
  firstName?: string | null;
  lastName?: string | null;
}

export interface UnitMapping {
  pKey_Unit_Mapping: number;
  unit_Name: string | null;
  floor: string | null;
  wing: string | null;
  unit: string | null;
  /** Per-unit flag — when true the Room/Bed editor switches
   *  to dropdowns sourced from `tblUnit_Room_Mapping`. Honored
   *  only when the facility-level `forceRoomBedSelection` is
   *  false; otherwise that override forces dropdowns for every
   *  unit regardless of this flag. */
  useRoomTray: boolean;
}

/** One row of `tblUnit_Room_Mapping` — the per-unit catalog of
 *  configured rooms and the comma-separated bed list each room
 *  holds. `Beds == "-999999"` is the sentinel for "no bed". */
export interface UnitRoomMapping {
  pKey_Unit_Room_Mapping: number;
  fKey_Unit_Mapping: number;
  room: number;
  beds: string | null;
}

export interface SpecialNeedRef {
  pKey_Special_Needs: number;
  name: string;
}

// Sort/filter constants (as const - no runtime enums due to erasableSyntaxOnly)

export const GRID_SORT = {
  FullName: 0,
  Location: 1,
} as const;

export type GridSortType = (typeof GRID_SORT)[keyof typeof GRID_SORT];

export const PATIENT_STATUS = {
  Active: 'Active',
  Discharged: 'Discharged',
  Template: 'Template',
  BulkOrder: 'Bulk Order',
  Deleted: 'Deleted',
} as const;

/** Compute patient status for display */
export function getPatientStatus(p: Patient): string {
  if (p.isDeleted) return PATIENT_STATUS.Deleted;
  if (p.actAsTemplate) return PATIENT_STATUS.Template;
  if (p.isBulkOrder) return PATIENT_STATUS.BulkOrder;
  if (p.discharged) return PATIENT_STATUS.Discharged;
  return PATIENT_STATUS.Active;
}

/** Compute BMI from weight (lbs) and height (inches) with the standard
 *  703 (CDC / NIH: lb x 703 / in^2), the same as the PDPM pages (PdpmDAL.cs)
 *  and the shared `BMI_Calculator` (DietechShareUtilsVBCore/Modules/Diet_Utils.vb),
 *  which used 705 until 2026-10-24. */
export const BMI_FACTOR = 703;
export function computeBMI(weight: number, heightInches: number): number | null {
  if (!weight || !heightInches || heightInches <= 0) return null;
  return (weight / (heightInches * heightInches)) * BMI_FACTOR;
}

/** Check if patient has any special devices assigned */
export function hasSpecialDevices(p: Patient): boolean {
  return (
    (p.fKey_Spec_Devices_1 ?? 0) > 0 ||
    (p.fKey_Spec_Devices_2 ?? 0) > 0 ||
    (p.fKey_Spec_Devices_3 ?? 0) > 0 ||
    (p.fKey_Spec_Devices_4 ?? 0) > 0 ||
    (p.fKey_Spec_Devices_5 ?? 0) > 0
  );
}

/** Get allergy FK values as an array (non-zero only) */
export function getAllergyFKs(p: Patient): number[] {
  return [
    p.fKey_Food_Allergy_1, p.fKey_Food_Allergy_2, p.fKey_Food_Allergy_3,
    p.fKey_Food_Allergy_4, p.fKey_Food_Allergy_5, p.fKey_Food_Allergy_6,
    p.fKey_Food_Allergy_7, p.fKey_Food_Allergy_8, p.fKey_Food_Allergy_9,
    p.fKey_Food_Allergy_10,
  ].filter((fk): fk is number => fk != null && fk > 0);
}

/** Get special device FK values as an array (non-zero only) */
export function getSpecialDeviceFKs(p: Patient): number[] {
  return [
    p.fKey_Spec_Devices_1, p.fKey_Spec_Devices_2, p.fKey_Spec_Devices_3,
    p.fKey_Spec_Devices_4, p.fKey_Spec_Devices_5,
  ].filter((fk): fk is number => fk != null && fk > 0);
}
